import { SelectInput, SelectInputItem, Switch, TextField } from '@ads/components-react';
import { Controller, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useGetApiV1PersonSharedUspContractTypeDropdown } from '@/shared/api/generated';
import { DatePicker, Skeleton, Textarea } from '@/shared/ui/atoms';
import { FormField } from '@/shared/ui/molecules';

import { numToStr, strToNum, type TabProps } from '../personFormSchema';
import { ProfileRoleCards } from './ProfileRoleCards';

// ─── Cards de sub-entidade ────────────────────────────────────────────────────

function StudentCard({ control, register }: Pick<TabProps, 'control' | 'register'>) {
  const { t } = useTranslation('personManagement');

  return (
    <div className="flex flex-col gap-mini rounded-md border-hairline border-ctx-layout-border p-mini">
      <h3 className="ts-heading-xs text-ctx-content-title">
        {t('personForm.fields.studentsSection')}
      </h3>
      <div className="grid grid-cols-1 gap-mini @2xl:grid-cols-3">
        <FormField label={t('personForm.fields.student.ra')}>
          <TextField fullWidth size="md" {...register('student.ra')} />
        </FormField>
        <FormField label={t('personForm.fields.student.codeUsp')}>
          <TextField fullWidth size="md" {...register('student.codeUsp')} />
        </FormField>
        <Controller
          name="student.perfilPublic"
          control={control}
          render={({ field }) => (
            <FormField label="">
              <Switch
                size="md"
                label={t('personForm.fields.student.perfilPublic')}
                checked={field.value === true}
                onCheckedChange={field.onChange}
              />
            </FormField>
          )}
        />
      </div>
      <Controller
        name="student.curriculum"
        control={control}
        render={({ field }) => (
          <FormField label={t('personForm.fields.student.curriculum')}>
            <Textarea
              value={(field.value as string) ?? ''}
              onChange={e => field.onChange(e.target.value)}
              rows={4}
            />
          </FormField>
        )}
      />
    </div>
  );
}

function EmployeeCard({ control, register }: Pick<TabProps, 'control' | 'register'>) {
  const { t } = useTranslation('personManagement');

  return (
    <div className="flex flex-col gap-mini rounded-md border-hairline border-ctx-layout-border p-mini">
      <h3 className="ts-heading-xs text-ctx-content-title">
        {t('personForm.fields.employeesSection')}
      </h3>
      <div className="flex flex-wrap gap-xs">
        <Controller
          name="employee.serviceRequestNotification"
          control={control}
          render={({ field }) => (
            <Switch
              label={t('personForm.fields.employee.serviceRequestNotification')}
              checked={field.value === true}
              onCheckedChange={field.onChange}
            />
          )}
        />
      </div>
      <div className="grid grid-cols-1 gap-mini @md:grid-cols-2">
        <FormField label={t('personForm.fields.employee.codeBadge')}>
          <TextField fullWidth size="md" {...register('employee.codeBadge')} />
        </FormField>
        <FormField label={t('personForm.fields.employee.postId')}>
          <TextField
            fullWidth
            size="md"
            type="number"
            {...register('employee.postId', { valueAsNumber: true })}
          />
        </FormField>
      </div>
      <div className="grid grid-cols-1 gap-mini @md:grid-cols-2">
        <Controller
          name="employee.dateStart"
          control={control}
          render={({ field }) => (
            <FormField label={t('personForm.fields.employee.dateStart')}>
              <DatePicker size="md" value={field.value ?? ''} onChange={field.onChange} />
            </FormField>
          )}
        />
        <Controller
          name="employee.dateFinish"
          control={control}
          render={({ field }) => (
            <FormField label={t('personForm.fields.employee.dateFinish')}>
              <DatePicker size="md" value={field.value ?? ''} onChange={field.onChange} />
            </FormField>
          )}
        />
      </div>
    </div>
  );
}

function TeachingStaffCard({ control, register }: Pick<TabProps, 'control' | 'register'>) {
  const { t } = useTranslation('personManagement');
  const { data: uspContractTypeOptions = [] } = useGetApiV1PersonSharedUspContractTypeDropdown();

  return (
    <div className="flex flex-col gap-mini rounded-md border-hairline border-ctx-layout-border p-mini">
      <h3 className="ts-heading-xs text-ctx-content-title">
        {t('personForm.fields.teachingStaffSection')}
      </h3>
      <div className="flex flex-wrap gap-xs">
        <Controller
          name="teachingStaff.isUspTeaching"
          control={control}
          render={({ field }) => (
            <Switch
              label={t('personForm.fields.teachingStaff.isUspTeaching')}
              checked={field.value === true}
              onCheckedChange={field.onChange}
            />
          )}
        />
        <Controller
          name="teachingStaff.isLattes"
          control={control}
          render={({ field }) => (
            <Switch
              label={t('personForm.fields.teachingStaff.isLattes')}
              checked={field.value === true}
              onCheckedChange={field.onChange}
            />
          )}
        />
      </div>
      <div className="grid grid-cols-1 gap-mini @md:grid-cols-2">
        <Controller
          name="teachingStaff.uspContractType"
          control={control}
          render={({ field }) => (
            <SelectInput
              size="md"
              label={t('personForm.fields.teachingStaff.uspContractType')}
              placeholder={t('personForm.fields.teachingStaff.uspContractType')}
              value={numToStr(field.value)}
              onValueChange={v => field.onChange(strToNum(v))}
            >
              {uspContractTypeOptions.map(({ key, value }) => (
                <SelectInputItem key={key} value={String(key)} custom={{ root: 'text-nowrap' }}>
                  {value ?? ''}
                </SelectInputItem>
              ))}
            </SelectInput>
          )}
        />
        <FormField label={t('personForm.fields.teachingStaff.codeUSP')}>
          <TextField fullWidth size="md" {...register('teachingStaff.codeUSP')} />
        </FormField>
      </div>
      <div className="grid grid-cols-1 gap-mini @md:grid-cols-2">
        <FormField label={t('personForm.fields.teachingStaff.certNumber')}>
          <TextField fullWidth size="md" {...register('teachingStaff.certNumber')} />
        </FormField>
        <FormField label={t('personForm.fields.teachingStaff.certWorkload')}>
          <TextField
            fullWidth
            size="md"
            type="number"
            {...register('teachingStaff.certWorkload', { valueAsNumber: true })}
          />
        </FormField>
      </div>
      <div className="grid grid-cols-1 gap-mini @md:grid-cols-2">
        <Controller
          name="teachingStaff.certExpirationDate"
          control={control}
          render={({ field }) => (
            <FormField label={t('personForm.fields.teachingStaff.certExpirationDate')}>
              <DatePicker size="md" value={field.value ?? ''} onChange={field.onChange} />
            </FormField>
          )}
        />
        <Controller
          name="teachingStaff.lattesUpdateDate"
          control={control}
          render={({ field }) => (
            <FormField label={t('personForm.fields.teachingStaff.lattesUpdateDate')}>
              <DatePicker size="md" value={field.value ?? ''} onChange={field.onChange} />
            </FormField>
          )}
        />
      </div>
      <Controller
        name="teachingStaff.observation"
        control={control}
        render={({ field }) => (
          <FormField label={t('personForm.fields.teachingStaff.observation')}>
            <Textarea
              value={(field.value as string) ?? ''}
              onChange={e => field.onChange(e.target.value)}
              rows={2}
            />
          </FormField>
        )}
      />
    </div>
  );
}

// ─── Tab principal ────────────────────────────────────────────────────────────

export interface ProfilesTabProps extends Pick<TabProps, 'control' | 'register'> {
  /** Quando true, exibe skeleton nos cards de detalhe enquanto os dados opcionais carregam */
  readonly isLoading?: boolean;
}

export function ProfilesTab({ control, register, isLoading = false }: ProfilesTabProps) {
  const { t } = useTranslation('personManagement');

  const isStudent = useWatch({ control, name: 'isStudent' });
  const isEmployee = useWatch({ control, name: 'isEmployee' });
  const isTeachingStaff = useWatch({ control, name: 'isTeachingStaff' });

  const hasAnyRole = isStudent || isEmployee || isTeachingStaff;

  return (
    <div className="flex flex-col gap-xxs">
      {/* Seleção de perfis — sempre visível, vem das queries principais */}
      <div className="flex flex-col gap-nano">
        <div className="flex flex-col gap-quark">
          <h3 className="ts-heading-xs text-ctx-content-title">
            {t('personForm.fields.profilesSection')}
          </h3>
          <p className="ts-paragraph-xxs text-ctx-content-base">
            {t('personForm.steps.profiles.description')}
          </p>
        </div>
        <ProfileRoleCards control={control} />
      </div>

      {/* Cards de detalhe: skeleton enquanto dados opcionais carregam */}
      {isLoading && hasAnyRole && (
        <div className="flex flex-col gap-xs">
          {isStudent && <Skeleton className="h-32 w-full rounded-sm" />}
          {isEmployee && <Skeleton className="h-32 w-full rounded-sm" />}
          {isTeachingStaff && <Skeleton className="h-48 w-full rounded-sm" />}
        </div>
      )}

      {!isLoading && (
        <>
          {isStudent && <StudentCard control={control} register={register} />}
          {isEmployee && <EmployeeCard control={control} register={register} />}
          {isTeachingStaff && <TeachingStaffCard control={control} register={register} />}
        </>
      )}
    </div>
  );
}
